import { Flame, Lock } from 'lucide-react';

/**
 * Streak milestone badge chips (3 / 7 / 30 / 100 days). Higher milestones
 * get hotter colors — a visible status symbol next to names and on profiles.
 */
const TIERS: Record<number, { label: string; className: string }> = {
  3: {
    label: '3-DAY',
    className: 'border-primary/50 text-primary bg-primary/10',
  },
  7: {
    label: '7-DAY',
    className: 'border-secondary/60 text-secondary bg-secondary/10',
  },
  30: {
    label: '30-DAY',
    className:
      'border-amber-400/70 text-amber-300 bg-amber-400/10 shadow-[0_0_8px_rgba(251,191,36,0.35)]',
  },
  100: {
    label: '100-DAY',
    className:
      'border-red-500/70 text-red-400 bg-red-500/10 shadow-[0_0_10px_rgba(239,68,68,0.45)]',
  },
};

export function StreakBadges({
  milestones,
  size = 'sm',
  className = '',
}: {
  milestones?: number[] | null;
  size?: 'sm' | 'md';
  className?: string;
}) {
  if (!milestones || milestones.length === 0) return null;
  const sorted = [...milestones].sort((a, b) => a - b);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      {sorted.map((m) => {
        const tier = TIERS[m] ?? {
          label: `${m}-DAY`,
          className: 'border-primary/50 text-primary bg-primary/10',
        };
        return (
          <span
            key={m}
            title={`${m}-day streak badge`}
            data-testid={`badge-streak-${m}`}
            className={`inline-flex shrink-0 items-center gap-1 rounded-none border font-mono font-bold uppercase tracking-widest ${
              size === 'md' ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'
            } ${tier.className}`}
          >
            <Flame className={size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            {tier.label}
          </span>
        );
      })}
    </span>
  );
}

const MILESTONES = [3, 7, 30, 100] as const;

/**
 * Full badge case for the profile page: shows all four streak milestones.
 * Earned badges are lit in their tier colors; unearned ones are greyed out
 * with a "X DAYS TO GO" countdown based on the current streak.
 */
export function BadgeCase({
  milestones,
  currentStreak = 0,
  className = '',
}: {
  milestones?: number[] | null;
  currentStreak?: number | null;
  className?: string;
}) {
  const earned = new Set(milestones ?? []);
  const streak = currentStreak ?? 0;
  return (
    <div className={`grid gap-4 grid-cols-2 lg:grid-cols-4 ${className}`}>
      {MILESTONES.map((m) => {
        const tier = TIERS[m];
        const isEarned = earned.has(m);
        const daysToGo = Math.max(m - streak, 0);
        return (
          <div
            key={m}
            data-testid={`badge-case-${m}`}
            className={`relative flex flex-col items-center gap-2 border p-5 text-center transition-all ${
              isEarned
                ? tier.className
                : 'border-muted-foreground/20 bg-muted/20 text-muted-foreground/50 grayscale'
            }`}
          >
            {isEarned ? (
              <Flame className="h-8 w-8" />
            ) : (
              <Lock className="h-8 w-8" data-testid={`badge-locked-${m}`} />
            )}
            <div className="font-display text-lg font-black uppercase tracking-widest">
              {tier.label}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest">
              {isEarned ? (
                <span data-testid={`badge-earned-label-${m}`}>UNLOCKED</span>
              ) : (
                <span data-testid={`badge-days-to-go-${m}`}>
                  {daysToGo} {daysToGo === 1 ? 'DAY' : 'DAYS'} TO GO
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
